export const GAZE_REPORT_TOKEN_SESSION_KEY = "gazeReportRequestToken";

const TOKEN_QUERY_KEYS = ["token", "accessToken"];
const REQUIRED_QUERY_KEYS = ["token", "seniorId", "reportId"];
const MAX_TOKEN_LENGTH = 8192;
const MAX_ID_LENGTH = 256;

const invalid = (code, message) => ({ ok: false, code, message });

const singleValue = (params, key) => {
  const values = params.getAll(key);
  if (values.length > 1) {
    return invalid(
      "duplicate_parameter",
      `The ${key} parameter was provided more than once.`,
    );
  }
  const value = values[0]?.trim() || "";
  if (!value) {
    return invalid("missing_parameter", `The ${key} parameter is required.`);
  }
  return { ok: true, value };
};

// The link may carry the token as either `token` or `accessToken`.
const queryToken = (params) => {
  for (const key of TOKEN_QUERY_KEYS) {
    if (params.getAll(key).length > 1) {
      return invalid(
        "duplicate_parameter",
        `The ${key} parameter was provided more than once.`,
      );
    }
  }
  const values = TOKEN_QUERY_KEYS.map(
    (key) => params.get(key)?.trim() || "",
  ).filter(Boolean);
  if (new Set(values).size > 1) {
    return invalid(
      "duplicate_parameter",
      "The token parameter was provided more than once.",
    );
  }
  return { ok: true, value: values[0] || "" };
};

const readStoredToken = (storage, seniorId, reportId) => {
  if (!storage) return "";
  try {
    const stored = JSON.parse(
      storage.getItem(GAZE_REPORT_TOKEN_SESSION_KEY) || "null",
    );
    if (
      stored?.seniorId === seniorId &&
      stored?.reportId === reportId &&
      typeof stored?.token === "string"
    ) {
      return stored.token;
    }
  } catch {
    return "";
  }
  return "";
};

export const clearStoredGazeReportToken = (storage = window.sessionStorage) => {
  storage?.removeItem(GAZE_REPORT_TOKEN_SESSION_KEY);
};

export const parseGazeReportRequest = (
  search,
  storage = window.sessionStorage,
) => {
  const params = new URLSearchParams(search || "");
  const seniorResult = singleValue(params, "seniorId");
  if (!seniorResult.ok) return seniorResult;
  const reportResult = singleValue(params, "reportId");
  if (!reportResult.ok) return reportResult;

  const seniorId = seniorResult.value;
  const reportId = reportResult.value;
  if (seniorId.length > MAX_ID_LENGTH || reportId.length > MAX_ID_LENGTH) {
    return invalid(
      "invalid_parameter",
      "The report link contains an invalid identifier.",
    );
  }

  const tokenResult = queryToken(params);
  if (!tokenResult.ok) return tokenResult;

  const tokenFromQuery = tokenResult.value;
  const token = tokenFromQuery || readStoredToken(storage, seniorId, reportId);
  if (!token) {
    return invalid("missing_parameter", "The token parameter is required.");
  }
  if (token.length > MAX_TOKEN_LENGTH) {
    return invalid("invalid_parameter", "The report token is invalid.");
  }

  if (tokenFromQuery && storage) {
    storage.setItem(
      GAZE_REPORT_TOKEN_SESSION_KEY,
      JSON.stringify({ token: tokenFromQuery, seniorId, reportId }),
    );
  }

  return {
    ok: true,
    request: { token, seniorId, reportId },
    tokenCameFromQuery: Boolean(tokenFromQuery),
  };
};

export const removeTokenFromSearch = (search) => {
  const params = new URLSearchParams(search || "");
  TOKEN_QUERY_KEYS.forEach((key) => params.delete(key));
  const next = params.toString();
  return next ? `?${next}` : "";
};

export const REQUIRED_GAZE_REPORT_QUERY_KEYS = REQUIRED_QUERY_KEYS;
export const GAZE_REPORT_TOKEN_QUERY_KEYS = TOKEN_QUERY_KEYS;
